import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { AccountTree as AccountTreeIcon, Login as LoginIcon, South as SouthIcon } from '@mui/icons-material';
import EncycdataLogo from '@components/layout/EncycdataLogo';

interface Member {
  name: string;
  role: string;
  specialization: string;
  photo: string;
}

interface Vertical {
  title: string;
  reportsTo: string;
  lead: Member;
  team: Member[];
  layout?: 'lead-team' | 'flat';
  sportGroups?: Array<{
    sport: string;
    members: Member[];
  }>;
}

const founders: Member[] = [
  { name: 'Lingesh M', role: 'Co-Founder, VP Engineering', specialization: 'Architect, System and Cloud', photo: '/team/Lingesh.jpg' },
  { name: 'Oliver Monarch', role: 'Co-Founder, Sports Director', specialization: 'Tennis Player, Coach, Doubles', photo: '/team/Oliver.jpg' },
  { name: 'Tim Wallace', role: 'Co-Founder, Sports Director', specialization: 'National Ice Hockey Player, Head Coach', photo: '/team/Tim.jpg' },
  { name: 'Shep Messing', role: 'Co-Founder, Sports Director', specialization: 'Football Player, Coach, League Owner', photo: '/team/Shep.jpg' },
];

const ceo: Member = {
  name: 'Chandrakandh M',
  role: 'Founder and Chief Executive Officer',
  specialization: 'Technologist, Infrastructure Solutions, Sports Director - Badminton',
  photo: '/team/Chandrakandh.jpg',
};

const verticals: Vertical[] = [
  {
    title: 'Global Sports Leadership',
    reportsTo: 'CEO',
    layout: 'flat',
    lead: { name: 'Kate Wallis', role: 'Sports Director', specialization: 'High Performance and Wellbeing, International Consultant, Gymnastic', photo: '/team/Kate.jpg' },
    team: [
      { name: 'Oliver Monarch', role: 'Co-Founder, Sports Director', specialization: 'Tennis Player, Coach, Doubles', photo: '/team/Oliver.jpg' },
      { name: 'Tim Wallace', role: 'Co-Founder, Sports Director', specialization: 'National Ice Hockey Player, Head Coach', photo: '/team/Tim.jpg' },
      { name: 'Shep Messing', role: 'Co-Founder, Sports Director', specialization: 'Football Player, Coach, League Owner', photo: '/team/Shep.jpg' },
      { name: 'Davraj Raut', role: 'Sports Advisor', specialization: 'Cricketer, Indian Cricket A Performance Analyst', photo: '/team/Devraj.jpg' },
    ],
  },
  {
    title: 'Sports Research Team',
    reportsTo: 'CEO',
    layout: 'flat',
    lead: { name: 'Lok Sainath', role: 'Research Lead - Badminton', specialization: 'National Player, Coaching', photo: '/team/Lok.jpg' },
    team: [],
    sportGroups: [
      {
        sport: 'Badminton Research',
        members: [
          { name: 'Lok Sainath', role: 'Research Lead - Badminton', specialization: 'National Player, Coaching', photo: '/team/Lok.jpg' },
          { name: 'Advaith Vanarase', role: 'Research Specialist - Badminton', specialization: 'National Player, Coaching, Sports Psychology', photo: '/team/Advait.jpg' },
        ],
      },
      {
        sport: 'Coaching Research',
        members: [
          { name: 'Joe Morrissey', role: 'Research Specialist - Coaching', specialization: 'Club Support, Coaching Environment', photo: '' },
        ],
      },
    ],
  },
  {
    title: 'Engineering Team',
    reportsTo: 'Lingesh M (Co-Founder, VP Engineering)',
    lead: { name: 'Lingesh M', role: 'VP Engineering', specialization: 'Architect, System and Cloud', photo: '/team/Lingesh.jpg' },
    team: [
      { name: 'Perumal S', role: 'Backend Developer', specialization: 'API and Services', photo: '' },
      { name: 'Vignesh K', role: 'Backend Developer', specialization: 'Cloud Backend and Integrations', photo: '' },
      { name: '', role: 'Backend Developer', specialization: 'Position to be filled', photo: '' },
      { name: '', role: 'DevOps Engineer', specialization: 'Position to be filled', photo: '' },
    ],
  },
];

const ProfileCard: React.FC<{ member: Member; accent?: string }> = ({ member, accent = '#d9dee8' }) => {
  const candidates = useMemo(() => {
    const base = (member.photo || '').replace(/\.(png|jpg|jpeg|webp)$/i, '');
    if (!base) return [];
    return [`${base}.png`, `${base}.jpg`, `${base}.jpeg`, `${base}.webp`];
  }, [member.photo, member.name]);
  const [srcIndex, setSrcIndex] = useState(0);
  const [showBlank, setShowBlank] = useState(candidates.length === 0);

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '1px solid #dfe3ea',
        bgcolor: '#fff',
        overflow: 'hidden',
        height: '100%',
      }}
      elevation={0}
    >
      {showBlank ? (
        <Box sx={{ width: '100%', height: 148, bgcolor: '#f3f4f6' }} />
      ) : (
        <Box
          component="img"
          src={candidates[srcIndex]}
          alt={member.name || 'Team member'}
          onError={() => {
            if (srcIndex < candidates.length - 1) {
              setSrcIndex((prev) => prev + 1);
            } else {
              setShowBlank(true);
            }
          }}
          sx={{
            width: '100%',
            height: 148,
            objectFit: 'contain',
            objectPosition: 'top center',
            display: 'block',
            bgcolor: '#f4f6f9',
          }}
        />
      )}
      <Box
        sx={{
          m: 1,
          mt: -2.5,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.95)',
          border: `2px solid ${accent}`,
          px: 1.2,
          py: 0.8,
          backdropFilter: 'blur(2px)',
        }}
      >
        {member.name ? (
          <Typography variant="body1" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
            {member.name}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {member.role}
        </Typography>
        <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700, display: 'block', mt: 0.2 }}>
          {member.specialization}
        </Typography>
      </Box>
    </Card>
  );
};

const VerticalHierarchy: React.FC<{ vertical: Vertical }> = ({ vertical }) => (
  <Card sx={{ border: '1px solid #dbe3ef', borderRadius: 3, p: 2 }} elevation={0}>
    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f4c81' }}>
      {vertical.title}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
      Reports to: {vertical.reportsTo}
    </Typography>

    <Box sx={{ mt: 1.5 }}>
      {vertical.sportGroups && vertical.sportGroups.length > 0 ? (
        <Stack spacing={2}>
          {vertical.sportGroups.map((group) => (
            <Box key={`${vertical.title}-${group.sport}`}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', mb: 1 }}>
                {group.sport}
              </Typography>
              <Grid container spacing={2}>
                {group.members.map((member) => (
                  <Grid item xs={12} sm={6} md={3} key={`${vertical.title}-${group.sport}-${member.name || member.role}`}>
                    <ProfileCard member={member} accent="#f4bf47" />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Stack>
      ) : vertical.layout === 'flat' ? (
        <Grid container spacing={2}>
          {[vertical.lead, ...vertical.team].map((member) => (
            <Grid item xs={12} sm={6} md={3} key={`${vertical.title}-${member.name || member.role}`}>
              <ProfileCard member={member} accent="#f4bf47" />
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} sm={6} md={4}>
          <ProfileCard member={vertical.lead} accent="#f4bf47" />
        </Grid>
      </Grid>
      <Box
        sx={{
          position: 'relative',
          my: 1.25,
          height: 28,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Box sx={{ position: 'absolute', left: '50%', top: 0, width: 2, height: 10, bgcolor: '#1976d2', transform: 'translateX(-50%)' }} />
        <Box sx={{ position: 'absolute', left: 20, right: 20, top: 10, height: 2, bgcolor: '#1976d2' }} />
      </Box>
      <Stack alignItems="center" sx={{ my: 0.5, display: { xs: 'flex', md: 'none' } }}>
        <SouthIcon sx={{ color: '#1976d2' }} />
      </Stack>
      <Grid container spacing={2} sx={{ position: 'relative' }}>
        {vertical.team.map((member) => (
          <Grid item xs={12} sm={6} md={3} key={`${vertical.title}-${member.name}`} sx={{ position: 'relative' }}>
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: -8,
                left: '50%',
                width: 2,
                height: 8,
                bgcolor: '#1976d2',
                transform: 'translateX(-50%)',
              }}
            />
            <ProfileCard member={member} />
          </Grid>
        ))}
      </Grid>
        </>
      )}
    </Box>
  </Card>
);

const TeamPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <AppBar position="sticky" sx={{ bgcolor: '#ffffff', boxShadow: 1 }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <EncycdataLogo height={20} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', cursor: 'pointer' }} onClick={() => navigate('/')}>
              EncycGames
            </Typography>
          </Box>
          <Button variant="text" onClick={() => navigate('/team')} sx={{ mr: 1, color: '#1976d2' }}>
            Team
          </Button>
          <Button variant="text" onClick={() => navigate('/pricing')} sx={{ mr: 1, color: '#1976d2' }}>
            Pricing
          </Button>
          <Button variant="contained" startIcon={<LoginIcon />} onClick={() => navigate('/login')} sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}>
            Sign in
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)', color: 'white', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <AccountTreeIcon />
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Team Structure and Reporting Hierarchy
            </Typography>
          </Stack>
          <Typography variant="h6" sx={{ fontWeight: 700, opacity: 0.98, mb: 0.5 }}>
            Our team of champions and experts who shapes 100% Sports Performance
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.95 }}>
            Cross-functional teams are grouped by domain and report directly to CEO.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Card sx={{ border: '1px solid #dbe3ef', borderRadius: 3, p: 2, mb: 2 }} elevation={0}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f4c81', mb: 1 }}>
            Founders and CEO
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <ProfileCard member={ceo} accent="#7aa6ff" />
            </Grid>
            {founders.map((member) => (
              <Grid item xs={12} sm={6} md={3} key={`founder-${member.name}`}>
                <ProfileCard member={member} accent="#f4bf47" />
              </Grid>
            ))}
          </Grid>
        </Card>

        <Stack alignItems="center" sx={{ mb: 2 }}>
          <SouthIcon sx={{ color: '#1976d2' }} />
        </Stack>

        <Stack spacing={2}>
          {verticals.map((vertical) => (
            <VerticalHierarchy key={vertical.title} vertical={vertical} />
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default TeamPage;
